/**
 * CampaignWorkflow.js
 *
 * WHAT: Multi-step campaign creation wizard orchestrating the four workflow steps.
 * WHY:  Creating a UAR campaign requires a strict sequence: refresh source data, prepare
 *       the attestation scope, assign attesters, and activate. This component manages step
 *       progression, locks completed campaigns for read-only viewing, and coordinates
 *       campaign closure and evidence generation.
 */
/* eslint-disable no-undef */
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import Step1DataRefresh from './CampaignWorkflow/Step1DataRefresh';
import AttestationPreparation from './CampaignWorkflow/Step2PrepareAttestation';
import UnifiedAttesterAssignment from './CampaignWorkflow/Step3AssignAttesters';
import AttestationPhase from './CampaignWorkflow/Step4SubmitAttestation';
import { CloseCampaignModal, EvidenceViewer } from './CampaignEvidence';

import API_CONFIG from '../config/api.config';
import { useAuth } from '../context/AuthContext';
import { StatusRibbon, CertificationStatusModal } from './common';

/**
 * WHAT: Stepper-based wizard that renders each campaign workflow step and tracks progression.
 * WHY:  Ensures administrators complete each prerequisite step (data refresh, scope prep,
 *       attester assignment) before activating the campaign for attestation, preventing
 *       incomplete or misconfigured campaigns from reaching managers.
 */
const CampaignWorkflow = ({ campaign, onBack }) => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [currentCampaign, setCurrentCampaign] = useState(campaign);
  const [statusRibbon, setStatusRibbon] = useState(null);
  // Cert-blocked modal: surfaced when update_campaign_status refuses the
  // Step 1 → Step 2 transition because the campaign's database_type has
  // uncertified or unassigned privileges/roles. Mirrors the modal that
  // Step1DataRefresh shows on the "Collect from Source" path so users get
  // a consistent block-and-explain experience regardless of which action
  // triggered the gate.
  const [showCertBlockedModal, setShowCertBlockedModal] = useState(false);
  const [certBlocked, setCertBlocked] = useState({});
  // Step-4 submit flow: Mark Complete on Step 4 runs a read-only attester-coverage
  // check (no status transition). Passing it reveals the Submit-Campaign button in
  // the sidebar; the real DRAFT/RUNNING → ATTESTATION transition only happens via
  // the Yes/Cancel confirm modal. A failed check opens a blocking error popup.
  const [step4ReadyToSubmit, setStep4ReadyToSubmit] = useState(false);
  const [step4GateError, setStep4GateError] = useState(null); // {title, details: []}
  // Reported up by the Step-4 panel (unmatched_users from attestation/prepare/):
  // how many in-scope users have NO attester. > 0 grays out Mark Complete on
  // Step 4 — a campaign with unassigned users must never reach ATTESTATION.
  const [step4UnassignedCount, setStep4UnassignedCount] = useState(0);
  const [showSubmitConfirmModal, setShowSubmitConfirmModal] = useState(false);
  const [isSubmittingAttestation, setIsSubmittingAttestation] = useState(false);
  // Use refs instead of state to prevent duplicate API calls - refs update synchronously
  const isPopulatingReportRef = useRef(false);
  const isClearingReportRef = useRef(false);

  // Sync currentCampaign with campaign prop when it changes
  useEffect(() => {
    if (campaign) {
      setCurrentCampaign(campaign);
    }
  }, [campaign]);

  // Reset the Step-4 submit reveal whenever the user leaves Step 4 or switches
  // campaigns — the coverage validation must be re-run for the new context
  // (data may have changed while they were away).
  useEffect(() => {
    setStep4ReadyToSubmit(false);
    // Also clear the unassigned count so a broken campaign doesn't keep Mark
    // Complete grayed out for the next one; the Step-4 panel re-reports on load,
    // and the check-attester-coverage popup backstops the brief unloaded window.
    setStep4UnassignedCount(0);
  }, [activeStep, campaign]);

  // State for Step 2 filters (lifted up from AttestationPreparation)
  const [userFilter, setUserFilter] = useState('all');
  const [accessFilters, setAccessFilters] = useState({
    writeAccess: false
  });
  // MSSQL only: whether the UAR admin wants system accounts (sa, dbo,
  // NT AUTHORITY\*, ##MS_*## certs, etc.) included in the attestation scope.
  // Default false = exclude them (matches the backend default). Threaded into
  // both populate-report calls so the Step-2 count and the Step-3 in-scope
  // count agree.
  const [includeSystemAccounts, setIncludeSystemAccounts] = useState(false);

  // State for auto-applying attester rules on Step 3
  const [attesterRulesAutoApplied, setAttesterRulesAutoApplied] = useState(false);

  // State for contextual help modal
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpStep, setHelpStep] = useState(null);

  // State for Step 2 success modal
  const [showStep2SuccessModal, setShowStep2SuccessModal] = useState(false);
  const [step2SuccessData, setStep2SuccessData] = useState(null);

  // State for campaign closure and evidence modals
  const [showCloseCampaignModal, setShowCloseCampaignModal] = useState(false);
  const [showEvidenceViewer, setShowEvidenceViewer] = useState(false);

  // Check if campaign is locked for attestation or completed
  const isLocked = currentCampaign && (currentCampaign.status === 'ATTESTATION' || currentCampaign.status === 'COMPLETED');

  // Initialize active step based on campaign's current_step
  useEffect(() => {
    console.log('CampaignWorkflow mounted with campaign:', campaign);

    // If campaign is in ATTESTATION or COMPLETED status, mark all steps as completed and lock editing
    if (campaign && (campaign.status === 'ATTESTATION' || campaign.status === 'COMPLETED')) {
      console.log(`Campaign is in ${campaign.status} status - locking workflow`);
      setActiveStep(6); // Set to final step
      setCompletedSteps([1, 2, 3, 4, 5, 6]); // Mark all steps as completed
      return;
    }

    if (campaign && campaign.current_step && campaign.current_step > 1) {
      // Set active step to the current step
      setActiveStep(campaign.current_step);
      // Mark all previous steps as completed
      const completed = [];
      for (let i = 1; i < campaign.current_step; i++) {
        completed.push(i);
      }
      setCompletedSteps(completed);
    }
  }, [campaign]);

  // One-click campaign auto-progression removed - user manually navigates between steps

  // Check for unmatched users when on Step 3 (Attester Assignment) and auto-apply rules
  useEffect(() => {
    const autoApplyRules = async () => {
      if (activeStep === 3 && campaign && campaign.campaign_id && !attesterRulesAutoApplied) {
        console.log('Auto-applying attester rules for Step 3...');
        setAttesterRulesAutoApplied(true);

        try {
          // Auto-apply attester assignment rules
          const response = await fetch(
            API_CONFIG.getDatabasesEndpoint('attestation/apply-all-attester-rules/'),
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ campaign_id: campaign.campaign_id })
            }
          );

          const data = await response.json();
          if (data.success) {
            console.log('Attester rules auto-applied successfully:', data.results);
          } else {
            console.error('Failed to auto-apply attester rules:', data.error);
          }
        } catch (error) {
          console.error('Error auto-applying attester rules:', error);
        }
      }
    };

    // Execute auto-apply rules
    autoApplyRules();
  }, [activeStep, campaign, attesterRulesAutoApplied]);

  const workflowSteps = [
    {
      id: 1,
      title: "Campaign Data Refresh",
      icon: "fas fa-sync-alt",
      description: "Verify and refresh all data sources before starting the UAR campaign",
      tasks: [
        "Refresh enterprise users from Azure AD",
        "Refresh Azure AD groups and memberships",
        "Refresh database user access data"
      ]
    },
    {
      id: 2,
      title: "Prepare for Attestation",
      icon: "fas fa-shield-alt",
      description: "Identify the users to be included in the attestation by selecting the account status and privilege level(s)",
      tasks: [
        "Select the users to be included in the Access Review"
      ]
    },
    {
      id: 3,
      title: "Attester Assignment",
      icon: "fas fa-users-cog",
      description: "Automatic attester assignment as described. Verify all users have assigned attesters",
      tasks: [
        "Automatic attester assignment as described. Verify all users have assigned attesters"
      ]
    },
    {
      id: 4,
      title: "Submit for Attestations",
      icon: "fas fa-clipboard-check",
      description: "Submit campaign for attestation and send notifications to attesters",
      tasks: [
        "Review users assigned for attestation",
        "Verify attester assignments are correct",
        "Submit campaign for attestation",
        "Send email notifications to all attesters"
      ]
    }
  ];

  // Contextual Help Content
  const stepHelpContent = {
    1: {
      title: "Verify Data Sources",
      overview: "Ensure all data sources are current before proceeding with the campaign.",
      sections: [
        {
          heading: "Enterprise Users",
          content: "Syncs user data from Azure AD including names, emails, departments, and manager relationships.",
          items: [
            "Verify last refresh is within 24 hours",
            "Confirm user count matches expected enterprise size",
            "Ensure manager assignments are current"
          ]
        },
        {
          heading: "Database User Access",
          content: "Refreshes database user access data from connected databases.",
          items: [
            "Collects user accounts, roles, and privileges",
            "Categorizes privilege levels (SUPER_ADMIN, ADMIN, WRITE, READ)",
            "Identifies active vs inactive accounts"
          ]
        }
      ],
      tips: [
        "Refresh data sources if they are more than 24 hours old",
        "Database refresh may take several minutes for large databases"
      ]
    },

    2: {
      title: "Select Users for Attestation",
      overview: "Review database users and select which ones will be included in the attestation campaign.",
      sections: [
        {
          heading: "User Selection",
          content: "All database users are displayed for review. Use filters to focus on specific groups.",
          items: [
            "Filter by account status (Active/All)",
            "Filter by privilege level",
            "Review and adjust scope as needed"
          ]
        },
        {
          heading: "Marking Users In/Out of Scope",
          content: "Users marked 'In Scope' will proceed to attester assignment. Users marked 'Out of Scope' will be excluded from the campaign.",
          items: [
            "In Scope - User will be included in the attestation",
            "Out of Scope - User will be excluded from this campaign"
          ]
        }
      ],
      tips: [
        "Focus on active accounts with elevated privileges",
        "Use filters to quickly review specific user groups"
      ]
    },

    3: {
      title: "Attester Assignment",
      overview: "Automatic attester assignment as described. Verify all users have assigned attesters.",
      sections: [
        {
          heading: "Assignment Rules",
          content: "Attesters are assigned based on the following priority:",
          items: [
            "1. User Manager - Employee's direct manager from Azure AD",
            "2. Account Owner - Designated owner for service/shared accounts",
            "3. Database Owner - DBA responsible for the database",
            "4. Application Owner - Application team lead"
          ]
        },
        {
          heading: "Apply Assignment Rules",
          content: "Click 'Apply Assignment Rules' to automatically assign attesters based on the priority order above.",
          items: [
            "Named users are assigned to their manager",
            "Service accounts are assigned to their account owner",
            "Remaining users fallback to database or application owner"
          ]
        }
      ],
      tips: [
        "Click 'Apply Assignment Rules' to assign attesters automatically",
        "Review users without attesters and assign manually if needed",
        "Ensure all in-scope users have an assigned attester before proceeding"
      ]
    },

    4: {
      title: "Review and Submit",
      overview: "Review the final list of users and attesters, then submit the campaign to begin attestation.",
      sections: [
        {
          heading: "Pre-Submission Review",
          items: [
            "Verify all in-scope users have assigned attesters",
            "Review user and attester assignments",
            "Confirm enterprise and database data is current"
          ]
        },
        {
          heading: "What Happens When You Submit",
          items: [
            "Campaign status changes to 'ATTESTATION'",
            "Campaign configuration becomes locked",
            "Email notifications are sent to all attesters",
            "Attesters can begin reviewing their assigned users"
          ]
        },
        {
          heading: "After Submission",
          items: [
            "Monitor attestation progress from the campaign dashboard",
            "Send reminders to attesters as needed",
            "Campaign completes when all attestations are submitted"
          ]
        }
      ],
      tips: [
        "Review the user list before submitting",
        "Submission cannot be undone - the campaign will be locked",
        "Consider notifying attesters before submitting"
      ]
    }
  };

  const handleStepClick = async (stepId) => {
    // Prevent navigation if campaign is locked (attestation or completed)
    if (isLocked) {
      console.log(`Campaign is locked (${campaign.status}) - navigation disabled`);
      return;
    }

    // Only allow navigation to step if it's accessible
    if (canAccessStep(stepId)) {
      setActiveStep(stepId);

      // If navigating to a previous step, reset all subsequent completed steps
      const currentActiveStep = activeStep;
      if (stepId < currentActiveStep) {
        // Remove all completed steps that are >= stepId
        const newCompletedSteps = completedSteps.filter(step => step < stepId);
        setCompletedSteps(newCompletedSteps);
        console.log(`Navigating to previous step ${stepId}. Resetting completed steps from ${stepId} onwards.`);
      }

      // Update campaign status when navigating to a new step
      if (campaign && campaign.campaign_id) {
        try {
          const stepTitle = workflowSteps.find(s => s.id === stepId)?.title || `Step ${stepId}`;
          // Preserve ATTESTATION or COMPLETED status if already set, otherwise set to running
          const newStatus = (currentCampaign && (currentCampaign.status === 'ATTESTATION' || currentCampaign.status === 'COMPLETED'))
            ? currentCampaign.status
            : 'running';
          const payload = {
            campaign_id: campaign.campaign_id,
            current_step: stepId,
            step_title: stepTitle,
            status: newStatus
          };
          console.log('Updating campaign status on step navigation:', payload);

          const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/update-status/'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
          });

          const result = await response.json();
          console.log('Campaign status update result:', result);

          // Update local campaign state
          if (result.success) {
            setCurrentCampaign({
              ...currentCampaign,
              status: newStatus.toUpperCase(),
              current_step: stepId,
              step_title: stepTitle
            });
          }

          // If navigating back to step 1 (Data Refresh), clear the report data
          // Use ref to prevent duplicate API calls
          if (stepId === 1 && campaign && campaign.campaign_id && !isClearingReportRef.current) {
            console.log('Navigating to Step 1 (Data Refresh) - Clearing uar_report table data...');
            isClearingReportRef.current = true;
            try {
              await clearCampaignReportData();
            } finally {
              isClearingReportRef.current = false;
            }
          }

          // If navigating to step 2 (Prepare for Attestation), create/clear uar_report table
          // and set all users to in-scope so they can be viewed and filtered
          // Use ref (not state) to prevent duplicate API calls - refs update synchronously
          if (stepId === 2 && campaign && campaign.campaign_id && !isPopulatingReportRef.current) {
            console.log('Navigating to Step 2 (Prepare for Attestation) - Creating/clearing uar_report table...');
            isPopulatingReportRef.current = true; // Set immediately to block concurrent calls
            try {
              const createTablePayload = {
                campaign_id: campaign.campaign_id,
                campaign_name: campaign.campaign_name
              };

              const createTableResponse = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/create-report-table/'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(createTablePayload)
              });

              const createTableResult = await createTableResponse.json();
              console.log('uar_report table creation/clear result:', createTableResult);

              // After creating the table, set all users to in-scope so they can be viewed
              // When Step 2 is marked complete, filters will be applied to update user_in_scope
              if (createTableResult.success || createTableResult.table_name) {
                console.log('Setting all users to in-scope for Step 2 viewing...');
                const populatePayload = {
                  // populate-report resolves the campaign by name (see the
                  // Step-2-complete call below). Sending the numeric campaign_id
                  // here made the lookup fail (UPPER(integer)) so user_in_scope
                  // stayed 'NO' for every row and Step 2 listed no users.
                  campaign_id: campaign.campaign_name,
                  user_filter: 'all',           // Include all users initially
                  write_access_only: false,     // No write filter
                  privilege_levels: [],         // No privilege filter - all levels included
                  include_system_accounts: includeSystemAccounts  // MSSQL: admin's system-account choice
                };

                const populateResponse = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/populate-report/'), {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify(populatePayload)
                });

                const populateResult = await populateResponse.json();
                console.log('Initial user_in_scope populate result:', populateResult);

                if (populateResult.success) {
                  console.log(`All ${populateResult.in_scope_users} users set to in-scope for viewing`);
                }
              }
            } catch (err) {
              console.error('Failed to create/populate uar_report table on navigation to Step 2:', err);
            } finally {
              isPopulatingReportRef.current = false;
            }
          }
        } catch (err) {
          console.error('Failed to update campaign status on navigation:', err);
        }
      }
    }
  };

  // Helper function to check if a step is editable
  // Steps 1-4 become read-only once attestation has been submitted or campaign is completed
  const isStepEditable = (stepId) => {
    // Check if campaign status is ATTESTATION or COMPLETED
    if (currentCampaign && (currentCampaign.status === 'ATTESTATION' || currentCampaign.status === 'COMPLETED')) {
      // All steps 1-4 are read-only after attestation submission or completion
      if (stepId >= 1 && stepId <= 4) {
        return false;
      }
    }

    // If campaign has reached Step 4 (final), steps 1-3 are read-only
    const currentMaxStep = Math.max(activeStep, ...completedSteps, 1);
    if (currentMaxStep >= 4 && stepId < 4) {
      return false;
    }

    return true;
  };

  // Function to refresh campaign data from backend
  const refreshCampaignData = async () => {
    if (!campaign || !campaign.campaign_id) return;

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`campaigns/${campaign.campaign_id}/`));
      const data = await response.json();

      if (data.success && data.campaign) {
        setCurrentCampaign(data.campaign);
        console.log('✅ Campaign data refreshed:', data.campaign.status);
      }
    } catch (err) {
      console.error('Failed to refresh campaign data:', err);
    }
  };

  /* WHAT: Submits the campaign for attestation — sends notification emails to every
     attester and transitions the campaign to ATTESTATION status.
     WHY: This is the point-of-no-return, reached only from the sidebar's Yes/Cancel
     confirm modal after Mark Complete's coverage validation passed (the old green
     ribbon inside Step 4 was removed). On success it re-enters markStepCompleted
     with fromSubmit=true so completedSteps / campaign status bookkeeping stays
     identical to the old flow. */
  const handleSubmitCampaignForAttestation = async () => {
    setShowSubmitConfirmModal(false);
    setIsSubmittingAttestation(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/submit/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          campaign_id: campaign.campaign_id,
          campaign_name: campaign.campaign_name
        })
      });
      const data = await response.json();

      if (data.success) {
        alert(`Attestation submitted successfully!\n\nEmails sent to ${data.attesters_notified || 0} attesters.\n\nCampaign status updated to "ATTESTATION".\n\nAll attestation data is tracked in: ${data.table_name || 'N/A'}`);
        setStep4ReadyToSubmit(false);
        await markStepCompleted(4, true);
      } else {
        // The backend gate re-checks coverage at submit time (data can change between
        // the Mark Complete validation and this click) — surface its message in the
        // same blocking popup and hide the Submit button until re-validated.
        setStep4ReadyToSubmit(false);
        setStep4GateError({
          title: 'Could not submit campaign for attestation',
          details: [
            data.error || `Server returned HTTP ${response.status}.`,
            'The campaign has NOT been submitted. Please resolve the issue and try again.'
          ]
        });
      }
    } catch (err) {
      setStep4ReadyToSubmit(false);
      setStep4GateError({
        title: 'Could not submit campaign for attestation',
        details: [
          err?.message || 'Network error while contacting the server.',
          'The campaign has NOT been submitted. Please check your connection and try again.'
        ]
      });
    } finally {
      setIsSubmittingAttestation(false);
    }
  };

  // fromSubmit: Step 4 is two-phase. A plain Mark Complete click (fromSubmit=false)
  // only runs the read-only attester-coverage check and either opens the blocking
  // error popup or reveals the Submit-Campaign button — it never transitions the
  // campaign. handleSubmitCampaignForAttestation calls back with fromSubmit=true
  // after attestation/submit/ succeeds so the normal update-status bookkeeping
  // (completedSteps, status ATTESTATION, refresh) still runs.
  const markStepCompleted = async (stepId, fromSubmit = false) => {
    console.log(`DEBUG - markStepCompleted called with stepId: ${stepId}, type: ${typeof stepId}`);

    if (!completedSteps.includes(stepId)) {
      // Update campaign status in backend FIRST; only mark the step complete locally
      // after the backend confirms success. Marking locally before the network call
      // caused Step 4 to appear complete in the UI even when the backend 403'd the
      // ATTESTATION transition because user_employee_id was missing from the payload.
      if (campaign && campaign.campaign_id) {
        try {
          const stepTitle = workflowSteps.find(s => s.id === stepId)?.title || `Step ${stepId}`;

          if (stepId === 4 && !fromSubmit) {
            const coverageResponse = await fetch(
              API_CONFIG.getDatabasesEndpoint(`attestation/check-attester-coverage/?campaign_id=${campaign.campaign_id}`)
            );
            const coverage = await coverageResponse.json();

            if (!coverage.success) {
              setStep4ReadyToSubmit(false);
              setStep4GateError({
                title: `Could not mark "${stepTitle}" complete`,
                details: [
                  coverage.error || `Server returned HTTP ${coverageResponse.status}.`,
                  'The step has NOT been saved. Please resolve the issue and try again.'
                ]
              });
              return;
            }

            console.log('✅ Attester coverage check passed — revealing Submit button');
            setStep4ReadyToSubmit(true);
            return;
          }
          // Set status based on step:
          // - Step 1: RUNNING (campaign has started)
          // - Step 4: ATTESTATION (final step - Submit for Attestations)
          // - Other steps: RUNNING (campaign in progress)
          let newStatus;
          if (stepId === 4) {
            newStatus = 'ATTESTATION';
          } else if (stepId === 1) {
            newStatus = 'RUNNING';  // Explicitly set to RUNNING when Step 1 is completed
          } else {
            newStatus = 'RUNNING';
          }

          const payload = {
            campaign_id: campaign.campaign_id,
            current_step: stepId,
            step_title: stepTitle,
            status: newStatus,
            user_employee_id: user?.employee_id || '',
            is_admin: user?.role?.is_dev_admin || false
          };
          console.log('Updating campaign status:', payload);

          const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/update-status/'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
          });

          const result = await response.json();
          console.log('Campaign status update result:', result);

          if (!result.success) {
            console.error('❌ Failed to update campaign status:', result);
            // Certification-blocked transitions get the same modal the refresh
            // path uses, so the user sees one consistent block-and-explain
            // experience instead of a separate danger ribbon for this case.
            if (result.status === 'cert_blocked') {
              setCertBlocked({
                databaseType: result.database_type,
                totalCount: result.total_count || 0,
                certifiedCount: result.certified_count || 0,
                uncertifiedCount: result.uncertified_count || 0,
                unassignedCount: result.unassigned_count || 0,
                roleUncertifiedCount: result.role_uncertified_count || 0,
                roleCertifiedCount: result.role_certified_count || 0,
                roleTotalCount: result.role_total_count || 0,
                roleUnassignedCount: result.role_unassigned_count || 0,
              });
              setShowCertBlockedModal(true);
              return;
            }
            setStatusRibbon({
              tone: 'danger',
              title: `Could not mark "${stepTitle}" complete`,
              details: [
                result.error || `Server returned HTTP ${response.status}.`,
                'The step has NOT been saved. Please resolve the issue and try again.'
              ]
            });
            return;
          }

          setCompletedSteps(prev => prev.includes(stepId) ? prev : [...prev, stepId]);
          setCurrentCampaign(prevCampaign => ({
            ...prevCampaign,
            status: newStatus.toUpperCase(),
            current_step: stepId,
            step_title: stepTitle
          }));
          console.log(`✅ Campaign status updated to: ${newStatus.toUpperCase()}`);

          // Refresh campaign data from backend to ensure UI is in sync
          await refreshCampaignData();

          // If Step 2 (Prepare for Attestation) is marked complete, populate uar_report table
          console.log(`DEBUG - Checking if stepId === 2: ${stepId} === 2 = ${stepId === 2}`);
          if (stepId === 2) {
            console.log('Step 2 condition matched! Populating uar_report table...');

            // Extract selected privilege levels from accessFilters (format: {ADMIN: true, READ: false, ...})
            const selectedPrivilegeLevels = Object.entries(accessFilters)
              .filter(([level, isChecked]) => isChecked === true)
              .map(([level]) => level);

            console.log(`DEBUG - userFilter: ${userFilter}, selectedPrivilegeLevels: ${selectedPrivilegeLevels.join(', ')}`);
            const populatePayload = {
              campaign_id: campaign.campaign_name,  // Use campaign_name, not campaign_id (numeric)
              user_filter: userFilter,  // 'all' or 'active'
              privilege_levels: selectedPrivilegeLevels,  // Array of selected privilege level names
              include_system_accounts: includeSystemAccounts  // MSSQL: admin's system-account choice
            };
            console.log('DEBUG - Populate payload:', populatePayload);

            const populateResponse = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/populate-report/'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(populatePayload)
            });

            const populateResult = await populateResponse.json();
            console.log('uar_report populate result:', populateResult);

            if (populateResult.success) {
              // Build filter description based on selected privilege levels
              let filterDesc = '';
              const allPossibleLevels = Object.keys(accessFilters);
              const isAllSelected = selectedPrivilegeLevels.length === allPossibleLevels.length;

              if (userFilter === 'active' && !isAllSelected) {
                filterDesc = `Active users with: ${selectedPrivilegeLevels.join(', ')}`;
              } else if (userFilter === 'active') {
                filterDesc = 'Active users only (all privilege levels)';
              } else if (!isAllSelected && selectedPrivilegeLevels.length > 0) {
                filterDesc = `Privilege levels: ${selectedPrivilegeLevels.join(', ')}`;
              } else {
                filterDesc = 'All users (no filters)';
              }

              // Show success modal instead of alert
              setStep2SuccessData({
                tableName: populateResult.table_name,
                filterDesc: filterDesc,
                totalUsers: populateResult.total_users,
                inScopeUsers: populateResult.in_scope_users,
                outOfScopeUsers: populateResult.out_of_scope_users
              });
              setShowStep2SuccessModal(true);

              // Automatically apply attester assignment rules after Step 2
              console.log('Automatically applying attester assignment rules...');
              const applyAtterstersPayload = {
                campaign_id: campaign.campaign_id
              };

              const applyAtterstersResponse = await fetch(API_CONFIG.getDatabasesEndpoint('attestation/apply-all-attester-rules/'), {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(applyAtterstersPayload)
              });

              const applyAtterstersResult = await applyAtterstersResponse.json();
              console.log('Apply attesters result:', applyAtterstersResult);

              if (applyAtterstersResult.success) {
                console.log('✅ Attester assignment rules applied successfully');
                // Show summary in console
                const results = applyAtterstersResult.results || {};
                console.log('User Managers:', results.enterprise_manager_count || 0);
                console.log('Manual Overrides:', results.override_count || 0);
                console.log('Database Owners:', results.database_owner_count || 0);
                console.log('Application Owners:', results.application_owner_count || 0);
                console.log('Unmatched:', results.unmatched_count || 0);
                console.log('Total In-Scope:', results.total_in_scope || 0);
              } else {
                console.error('❌ Failed to apply attester assignment rules:', applyAtterstersResult.error);
                alert(`Warning: Attesters were not automatically assigned.\n\nError: ${applyAtterstersResult.error}\n\nYou can manually apply them in Step 3.`);
              }
            } else {
              alert(`Failed to update USER_IN_SCOPE flags: ${populateResult.error}`);
            }
          }
        } catch (err) {
          console.error('Failed to update campaign status:', err);
          setStatusRibbon({
            tone: 'danger',
            title: `Could not mark step complete`,
            details: [
              err?.message || 'Network error while contacting the server.',
              'The step has NOT been saved. Please check your connection and try again.'
            ]
          });
          return;
        }
      } else {
        console.log('No campaign selected or campaign_id missing');
        return;
      }

      // Auto-advance to next step if not the last step
      const nextStep = stepId + 1;
      if (nextStep <= workflowSteps.length) {
        console.log(`Auto-advancing to step ${nextStep}`);
        // Directly set active step (canAccessStep would fail because completedSteps state hasn't updated yet)
        setActiveStep(nextStep);

        // Trigger Step 2 report table creation if advancing to step 2
        if (nextStep === 2 && campaign && campaign.campaign_id && !isPopulatingReportRef.current) {
          console.log('Auto-advance to Step 2 - Creating/clearing uar_report table...');
          isPopulatingReportRef.current = true;
          try {
            const createTablePayload = {
              campaign_id: campaign.campaign_id,
              campaign_name: campaign.campaign_name
            };
            await fetch(API_CONFIG.getDatabasesEndpoint('attestation/create-report-table/'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(createTablePayload)
            });
          } catch (err) {
            console.error('Failed to create uar_report table on auto-advance:', err);
          } finally {
            isPopulatingReportRef.current = false;
          }
        }
      }
    }
  };

  const getStepStatus = (stepId) => {
    // If campaign is in ATTESTATION or COMPLETED status, show all steps as available (read-only)
    if (currentCampaign && (currentCampaign.status === 'ATTESTATION' || currentCampaign.status === 'COMPLETED')) {
      if (stepId === activeStep) return 'active';
      return 'available'; // All steps are accessible in read-only mode
    }

    // Normal mode
    if (completedSteps.includes(stepId)) return 'completed';
    if (stepId === activeStep) return 'active';
    // Only allow access to step if previous step is completed (or it's step 1)
    if (stepId === 1 || completedSteps.includes(stepId - 1)) return 'available';
    return 'locked';
  };

  const canAccessStep = (stepId) => {
    // If campaign is in ATTESTATION or COMPLETED status, allow access to all steps (read-only mode)
    if (currentCampaign && (currentCampaign.status === 'ATTESTATION' || currentCampaign.status === 'COMPLETED')) {
      return true;
    }
    // Otherwise, normal sequential access (must complete previous step)
    return stepId === 1 || completedSteps.includes(stepId - 1);
  };

  const getProgressPercentage = () => {
    // If campaign is in ATTESTATION or COMPLETED status, show 100% completion
    if (currentCampaign && (currentCampaign.status === 'ATTESTATION' || currentCampaign.status === 'COMPLETED')) {
      return 100;
    }
    return (completedSteps.length / workflowSteps.length) * 100;
  };



  // Fetch AD Groups refresh status

  // Handle AD Groups sync



  // Poll for refresh job completion

  // Trigger actual database refresh job (connects to source databases)

  // Quick reload stats from PostgreSQL (no source database connection)

  // Clear campaign report data
  const clearCampaignReportData = async () => {
    if (!campaign || !campaign.campaign_id) return;

    try {
      console.log(`Clearing report data for campaign ${campaign.campaign_id}...`);

      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint('campaigns/clear-report-data/'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ campaign_id: campaign.campaign_id })
        }
      );

      const data = await response.json();

      if (data.success) {
        console.log(`✅ ${data.message}`);
      } else {
        console.warn(`⚠️ Clear report data: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error clearing campaign report data:', error);
    }
  };


  // Help Modal Component
  const HelpModal = ({ step, onClose }) => {
    if (!step || !stepHelpContent[step]) return null;

    const help = stepHelpContent[step];

    return ReactDOM.createPortal(
      <div tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ margin: 0, maxWidth: '900px', width: '100%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
          <div style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '2px solid #333' }}>
            {/* Header */}
            <div style={{ flexShrink: 0, backgroundColor: '#2196f3', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '20px', borderBottom: '2px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 style={{ color: '#fff', fontWeight: '600', margin: 0, fontSize: '1.5rem' }}>
                  <i className="fas fa-question-circle me-2"></i>
                  Step {step}: {help.title}
                </h5>
                <p style={{ color: '#fff', margin: '8px 0 0 0', opacity: 0.9 }}>{help.overview}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{ background: 'transparent', border: 'none', fontSize: '28px', fontWeight: '700', lineHeight: 1, color: '#fff', cursor: 'pointer', padding: '0', opacity: 0.8 }}
                onMouseOver={(e) => e.target.style.opacity = '1'}
                onMouseOut={(e) => e.target.style.opacity = '0.8'}
              >×</button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', flex: '1 1 auto', backgroundColor: '#ffffff', padding: '24px' }}>
              {help.sections.map((section, idx) => (
                <div key={idx} style={{ marginBottom: '24px' }}>
                  <h6 style={{ color: '#2196f3', fontWeight: '600', fontSize: '1.1rem', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                    <i className="fas fa-chevron-right me-2" style={{ fontSize: '0.8rem' }}></i>
                    {section.heading}
                  </h6>

                  {section.content && (
                    <p style={{ color: '#424242', marginBottom: '12px', lineHeight: '1.6' }}>
                      {section.content}
                    </p>
                  )}

                  {section.items && (
                    <ul style={{ color: '#616161', marginBottom: '12px', paddingLeft: '24px' }}>
                      {section.items.map((item, i) => (
                        <li key={i} style={{ marginBottom: '8px', lineHeight: '1.6' }}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {help.tips && help.tips.length > 0 && (
                <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '16px', marginTop: '24px' }}>
                  <h6 style={{ color: '#856404', fontWeight: '600', marginBottom: '12px', display: 'flex', alignItems: 'center' }}>
                    <i className="fas fa-lightbulb me-2"></i>
                    Pro Tips
                  </h6>
                  <ul style={{ color: '#856404', marginBottom: '0', paddingLeft: '24px' }}>
                    {help.tips.map((tip, i) => (
                      <li key={i} style={{ marginBottom: '8px', lineHeight: '1.6' }}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ flexShrink: 0, backgroundColor: '#f8f9fa', borderTop: '2px solid #dee2e6', padding: '16px', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onClose}
                style={{ backgroundColor: '#2196f3', border: '1px solid #2196f3', borderRadius: '4px', color: '#fff', padding: '10px 24px', fontWeight: '500', cursor: 'pointer', fontSize: '1rem' }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#1976d2'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#2196f3'}
              >
                <i className="fas fa-check me-2"></i>
                Got it!
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="container-fluid">
      <style>
        {`
          @keyframes slowBlink {
            0%, 100% {
              opacity: 1;
              filter: brightness(1);
            }
            50% {
              opacity: 0.6;
              filter: brightness(1.3);
            }
          }
          .blink-next-step {
            animation: slowBlink 2s ease-in-out infinite !important;
          }
        `}
      </style>
      <div className="row">
        {/* Header */}
        <div className="col-12">
          {statusRibbon && (
            <StatusRibbon
              tone={statusRibbon.tone}
              title={statusRibbon.title}
              details={statusRibbon.details}
              onDismiss={() => setStatusRibbon(null)}
            />
          )}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button
              className="btn btn-outline-secondary"
              onClick={onBack}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to Campaigns
            </button>
            <div>
              <h2 className="text-primary mb-2">
                <i className="fas fa-flag me-2"></i>
                Campaign Workflow
              </h2>
              {currentCampaign && currentCampaign.campaign_description && (
                <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                  {currentCampaign.campaign_description}
                </p>
              )}
            </div>
            <div></div>
          </div>
        </div>

        {/* Vertical Progress Bar Sidebar - Compact */}
        <div className="col-lg-2 mb-4">
          <div className="card" style={{position: 'sticky', top: '20px', maxHeight: 'calc(100vh - 40px)', overflowY: 'auto'}}>
            <div className="card-body p-2">
              {/* Campaign Name at Top - Compact */}
              {currentCampaign && (
                <div className="mb-2 pb-2" style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <div className="small text-muted" style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                    Campaign
                  </div>
                  <div className="fw-bold text-truncate" style={{ color: 'var(--text-primary)', fontSize: '0.8rem' }} title={currentCampaign.campaign_name}>
                    {currentCampaign.campaign_name}
                  </div>
                </div>
              )}

              <div className="mb-2">
                <div className="mb-1" style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                  <i className="fas fa-tasks me-1"></i>
                  Steps
                  {currentCampaign && currentCampaign.status === 'ATTESTATION' && (
                    <i className="fas fa-lock ms-1 text-warning" title="Campaign locked in attestation"></i>
                  )}
                  {currentCampaign && currentCampaign.status === 'COMPLETED' && (
                    <i className="fas fa-lock ms-1 text-success" title="Campaign completed and locked"></i>
                  )}
                </div>
                <div className="progress" style={{height: '6px'}}>
                  <div
                    className="progress-bar bg-primary"
                    role="progressbar"
                    style={{width: `${getProgressPercentage()}%`}}
                    aria-valuenow={getProgressPercentage()}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
                <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                  {completedSteps.length}/{workflowSteps.length} ({Math.round(getProgressPercentage())}%)
                </small>
              </div>

              {/* Vertical Step Indicators - Card Style */}
              <div>
                {workflowSteps.map((step, index) => {
                  const status = getStepStatus(step.id);
                  const isLocked = status === 'locked';
                  const isReadOnly = !isStepEditable(step.id);
                  const isCompleted = status === 'completed';
                  const isActive = status === 'active';

                  return (
                    <div
                      key={step.id}
                      onClick={() => !isLocked && handleStepClick(step.id)}
                      className="mb-2"
                      style={{
                        padding: '8px 10px',
                        borderRadius: 'var(--radius-md)',
                        backgroundColor: isActive ? 'var(--primary-500)' : isCompleted ? 'var(--success-light)' : 'var(--background-secondary)',
                        border: isActive ? '2px solid var(--primary-500)' : isCompleted ? '2px solid var(--success)' : '2px solid var(--border-light)',
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        opacity: isLocked ? 0.6 : 1,
                        transition: 'var(--transition-base)',
                        boxShadow: isActive ? 'var(--shadow-md)' : 'none'
                      }}
                      onMouseEnter={(e) => {
                        if (!isLocked && !isActive) {
                          e.currentTarget.style.backgroundColor = 'var(--primary-50)';
                          e.currentTarget.style.borderColor = 'var(--primary-500)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isLocked && !isActive) {
                          e.currentTarget.style.backgroundColor = isCompleted ? 'var(--success-light)' : 'var(--background-secondary)';
                          e.currentTarget.style.borderColor = isCompleted ? 'var(--success)' : 'var(--border-light)';
                        }
                      }}
                    >
                      <div className="d-flex align-items-center">
                        {/* Circle Icon - Compact */}
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: '28px',
                            height: '28px',
                            backgroundColor: isActive ? 'rgba(255, 255, 255, 0.25)' : isCompleted ? 'var(--success)' : isLocked ? 'var(--gray-600)' : 'var(--gray-300)',
                            color: isActive || isCompleted || isLocked ? 'white' : 'var(--gray-600)',
                            border: isActive ? '2px solid white' : isCompleted ? '2px solid var(--success)' : '2px solid var(--gray-300)',
                            fontWeight: 'bold',
                            fontSize: '12px'
                          }}
                        >
                          {isCompleted ? (
                            <i className="fas fa-check"></i>
                          ) : isLocked ? (
                            <i className="fas fa-lock"></i>
                          ) : (
                            step.id
                          )}
                        </div>

                        {/* Step Title and Status - Compact */}
                        <div className="flex-grow-1 ms-2">
                          <div
                            style={{
                              fontSize: '11px',
                              fontWeight: '600',
                              color: isActive ? 'white' : isCompleted ? 'var(--success)' : isLocked ? 'var(--gray-600)' : 'var(--text-primary)',
                              lineHeight: '1.3',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                          >
                            <span>{step.title}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setHelpStep(step.id);
                                setShowHelpModal(true);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: isActive ? 'white' : 'var(--primary-500)',
                                cursor: 'pointer',
                                padding: '2px 4px',
                                fontSize: '11px',
                                opacity: 0.8,
                                transition: 'opacity 0.2s'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                              title={`Help for ${step.title}`}
                            >
                              <i className="fas fa-question-circle"></i>
                            </button>
                          </div>
                          <div
                            style={{
                              fontSize: '9px',
                              color: isActive ? 'rgba(255, 255, 255, 0.85)' : 'var(--text-muted)',
                              marginTop: '1px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px',
                              fontWeight: '500'
                            }}
                          >
                            {isCompleted ? 'DONE' : isActive ? 'ACTIVE' : isLocked ? 'LOCKED' : 'PENDING'}
                          </div>
                        </div>
                      </div>

                      {/* Mark Complete Button for Active Step */}
                      {isActive && !completedSteps.includes(step.id) && step.id !== 8 && (
                        <div className="mt-2">
                          <button
                            className="btn btn-success btn-sm w-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              markStepCompleted(step.id);
                            }}
                            disabled={!isStepEditable(step.id) || (step.id === 4 && step4UnassignedCount > 0)}
                            title={
                              step.id === 4 && step4UnassignedCount > 0
                                ? `${step4UnassignedCount} in-scope user(s) have no assigned attester — fix the assignments and re-run Step 3 before this step can be completed`
                                : !isStepEditable(step.id)
                                  ? 'This step cannot be modified after attestation has been submitted'
                                  : ''
                            }
                            style={{fontSize: '12px', padding: '6px'}}
                          >
                            <i className="fas fa-check me-1"></i>
                            Mark Complete
                          </button>
                        </div>
                      )}

                      {/* Step 4 only: Submit button revealed once Mark Complete's
                          attester-coverage validation passes. This is now the ONLY
                          UI path into ATTESTATION (green ribbon removed from the
                          Step 4 panel). Opens a Yes/Cancel confirm modal. */}
                      {step.id === 4 && isActive && step4ReadyToSubmit && !completedSteps.includes(4) && (
                        <div className="mt-2">
                          <button
                            className="btn btn-primary btn-sm w-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowSubmitConfirmModal(true);
                            }}
                            disabled={isSubmittingAttestation}
                            style={{fontSize: '12px', padding: '6px'}}
                          >
                            {isSubmittingAttestation ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Submitting...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-paper-plane me-1"></i>
                                Submit Campaign for Attestation
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Status at Bottom */}
              {currentCampaign && (
                <div className="mt-3 pt-3" style={{ borderTop: '2px solid var(--border-light)' }}>
                  <div className="small text-muted mb-2" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Campaign Status
                  </div>
                  <div>
                    <span className={`badge ${
                      currentCampaign.status === 'COMPLETED' || currentCampaign.status === 'completed' ? 'bg-success' :
                      currentCampaign.status === 'RUNNING' || currentCampaign.status === 'running' ? 'bg-primary' :
                      currentCampaign.status === 'ATTESTATION' ? 'bg-warning' :
                      currentCampaign.status === 'FAILED' ? 'bg-danger' :
                      currentCampaign.status === 'PAUSED' ? 'bg-warning' :
                      currentCampaign.status === 'CANCELLED' ? 'bg-secondary' :
                      'bg-info'
                    }`} style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
                      {currentCampaign.status}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step Details - Main Content Area (Expanded) */}
        <div className="col-lg-10">
          {/* Warning Banner for ATTESTATION or COMPLETED Status */}
          {isLocked && (
            <div className="mb-3">
              <div className={`alert border-0 shadow-sm mb-0`} role="alert" style={{
                background: currentCampaign.status === 'COMPLETED'
                  ? 'var(--success)'
                  : 'var(--warning)',
                color: '#212529'
              }}>
                <div className="d-flex align-items-center">
                  <i className="fas fa-lock fa-2x me-3"></i>
                  <div className="flex-grow-1">
                    <h6 className="alert-heading mb-1 fw-bold">
                      <i className={`fas ${currentCampaign.status === 'COMPLETED' ? 'fa-check-circle' : 'fa-exclamation-triangle'} me-2`}></i>
                      Campaign Locked - {currentCampaign.status === 'COMPLETED' ? 'Completed' : 'In Attestation'}
                    </h6>
                    <p className="mb-0 small">
                      {currentCampaign.status === 'COMPLETED' ? (
                        <>This campaign is <strong>COMPLETED</strong>. All attestations have been submitted and certified.
                        The campaign is now read-only and cannot be modified.</>
                      ) : (
                        <>This campaign is currently in <strong>ATTESTATION</strong> status.
                        Workflow editing is disabled and all configuration steps are marked as complete.
                        Attesters can now review and certify user access.</>
                      )}
                    </p>
                  </div>
                  {/* Campaign Actions */}
                  {currentCampaign.status === 'COMPLETED' && (
                    <div className="ms-3 d-flex gap-2">
                      <button
                        className="btn btn-light btn-sm"
                        onClick={() => setShowEvidenceViewer(true)}
                      >
                        <i className="fas fa-folder-open me-1"></i>
                        View Evidence
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step Header with Key Tasks */}
          <div className="card border-primary mb-4">
            <div className="card-header bg-primary text-white">
              {workflowSteps.find(s => s.id === activeStep) && (
                <div>
                  <h5 className="mb-2">
                    <i className={`${workflowSteps.find(s => s.id === activeStep).icon} me-2`}></i>
                    Step {activeStep}: {workflowSteps.find(s => s.id === activeStep).title}
                  </h5>

                  {/* Step Description */}
                  {!isLocked && (
                    <div className="mt-1">
                      <span className="small" style={{opacity: 0.9}}>
                        {workflowSteps.find(s => s.id === activeStep).description}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {workflowSteps.map(step => (
            step.id === activeStep && (
              <div key={step.id} className="card">
                <div className="card-body">

                  {step.id === 1 && (
                    <Step1DataRefresh campaign={currentCampaign} activeStep={activeStep} />
                  )}

                  {step.id === 2 && (
                    <AttestationPreparation
                      key={activeStep}
                      campaign={currentCampaign}
                      activeStep={activeStep}
                      userFilter={userFilter}
                      setUserFilter={setUserFilter}
                      accessFilters={accessFilters}
                      setAccessFilters={setAccessFilters}
                      includeSystemAccounts={includeSystemAccounts}
                      setIncludeSystemAccounts={setIncludeSystemAccounts}
                    />
                  )}

                  {step.id === 3 && (
                    <div className="mb-4">
                      <UnifiedAttesterAssignment key={`${activeStep}-attesters`} campaign={currentCampaign} activeStep={activeStep} step2ModalOpen={showStep2SuccessModal} />
                    </div>
                  )}

                  {step.id === 4 && (
                    <div className="mb-4">
                      <AttestationPhase
                        key={`${activeStep}-attestation`}
                        campaign={currentCampaign}
                        activeStep={activeStep}
                        onUnassignedCountChange={setStep4UnassignedCount}
                      />
                    </div>
                  )}

                  <div className="mt-4 p-3 bg-light rounded">
                    <h6 className="fw-semibold mb-2">
                      <i className="fas fa-lightbulb text-warning me-2"></i>
                      Guidelines:
                    </h6>
                    <div className="text-muted">
                      {step.id === 1 && "Verify that enterprise users and database user access data are current before proceeding."}
                      {step.id === 2 && "Use the status and privilege level filters to define which users are included in this attestation campaign. All matching users will be carried forward to the next step."}
                      {step.id === 3 && "Click 'Apply Assignment Rules' to assign attesters automatically. Verify all in-scope users have an assigned attester."}
                      {step.id === 4 && "Review the final list and submit the campaign. This will lock the configuration and send notifications to attesters."}
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Contextual Help Modal */}
      {showHelpModal && (
        <HelpModal
          step={helpStep}
          onClose={() => {
            setShowHelpModal(false);
            setHelpStep(null);
          }}
        />
      )}

      {/* Step 2 Success Modal */}
      {showStep2SuccessModal && step2SuccessData && ReactDOM.createPortal(
        <div
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={() => setShowStep2SuccessModal(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              maxWidth: '500px',
              width: '100%',
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              backgroundColor: '#28a745',
              color: '#fff',
              padding: '16px 20px',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h5 style={{ margin: 0, fontWeight: 600 }}>
                <i className="fas fa-check-circle me-2"></i>
                Step 2 Complete
              </h5>
              <button
                type="button"
                onClick={() => setShowStep2SuccessModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#fff',
                  fontSize: '24px',
                  cursor: 'pointer',
                  lineHeight: 1
                }}
              >×</button>
            </div>
            <div style={{ padding: '20px' }}>
              <div className="alert alert-success mb-3" style={{ marginBottom: '16px' }}>
                <i className="fas fa-flag-checkered me-2"></i>
                <strong>USER_IN_SCOPE Flags Updated Successfully!</strong>
              </div>

              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td style={{ fontWeight: 500, width: '40%' }}>Table</td>
                    <td><code>{step2SuccessData.tableName}</code></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Filter Applied</td>
                    <td>{step2SuccessData.filterDesc}</td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>Total Users</td>
                    <td><strong>{step2SuccessData.totalUsers}</strong></td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>
                      <span className="text-success">
                        <i className="fas fa-check me-1"></i>In-Scope Users
                      </span>
                    </td>
                    <td>
                      <span className="text-success fw-bold">{step2SuccessData.inScopeUsers}</span>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: 500 }}>
                      <span className="text-secondary">
                        <i className="fas fa-times me-1"></i>Out-of-Scope Users
                      </span>
                    </td>
                    <td>
                      <span className="text-secondary">{step2SuccessData.outOfScopeUsers}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div style={{
              padding: '12px 20px',
              borderTop: '1px solid #dee2e6',
              textAlign: 'right',
              backgroundColor: '#f8f9fa',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px'
            }}>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => setShowStep2SuccessModal(false)}
              >
                <i className="fas fa-arrow-right me-2"></i>
                Continue to Step 3
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Close Campaign Modal */}
      <CloseCampaignModal
        campaign={currentCampaign}
        show={showCloseCampaignModal}
        onClose={() => setShowCloseCampaignModal(false)}
        onClosed={(result) => {
          // Refresh campaign data to update status
          setCurrentCampaign(prev => ({
            ...prev,
            status: 'COMPLETED'
          }));
          // Close the modal and show evidence viewer
          setShowCloseCampaignModal(false);
          setShowEvidenceViewer(true);
        }}
      />

      {/* Evidence Viewer Modal */}
      <EvidenceViewer
        campaign={currentCampaign}
        show={showEvidenceViewer}
        onClose={() => setShowEvidenceViewer(false)}
      />

      {/* Certification gate: surfaced when update_campaign_status refuses the
          Step 1 → Step 2 transition. Same component the refresh-path modal
          uses (Step1DataRefresh) so the experience is identical no matter
          which action tripped the gate. */}
      <CertificationStatusModal
        show={showCertBlockedModal}
        onClose={() => setShowCertBlockedModal(false)}
        variant="error"
        title="Cannot Advance Campaign"
        databaseType={certBlocked.databaseType}
        totalCount={certBlocked.totalCount || 0}
        certifiedCount={certBlocked.certifiedCount || 0}
        uncertifiedCount={certBlocked.uncertifiedCount || 0}
        alertMessage={(() => {
          const pu = certBlocked.uncertifiedCount || 0;
          const ru = certBlocked.roleUncertifiedCount || 0;
          const rUnassigned = certBlocked.roleUnassignedCount || 0;
          const parts = [];
          if (pu > 0) parts.push(<><strong>{pu}</strong> privilege{pu === 1 ? '' : 's'}</>);
          if (ru > 0) parts.push(<><strong>{ru}</strong> role{ru === 1 ? '' : 's'}</>);
          return (
            <>
              {parts.length === 0
                ? <>Certification for <strong>{certBlocked.databaseType}</strong> is incomplete.</>
                : <>{parts.reduce((a, b) => <>{a} and {b}</>)} for{' '}
                    <strong>{certBlocked.databaseType}</strong>{' '}
                    {(pu + ru) === 1 ? 'is' : 'are'} not yet certified.</>}
              {rUnassigned > 0 && <> <strong>{rUnassigned}</strong> of those role{rUnassigned === 1 ? '' : 's'} still need{rUnassigned === 1 ? 's' : ''} a privilege level assigned before {rUnassigned === 1 ? 'it' : 'they'} can be certified.</>}
              <> Advancing this campaign past Step 1 would source data into an uncertified privilege matrix, so it is blocked until certification is complete.</>
            </>
          );
        })()}
        footer={
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowCertBlockedModal(false)}
          >
            <i className="fas fa-times me-2"></i>
            Close
          </button>
        }
      >
        <div className="alert alert-info mb-0" style={{ border: 'none' }}>
          <div className="d-flex align-items-start">
            <i className="fas fa-lightbulb fa-lg me-3 mt-1"></i>
            <div>
              <h6 className="alert-heading mb-2">Next Steps</h6>
              <ol className="mb-0 ps-3">
                <li className="mb-1">Navigate to <strong>"Admin Settings → Manage Privilege Certification"</strong></li>
                <li className="mb-1">Assign and certify all <strong>{certBlocked.databaseType}</strong> privileges and roles</li>
                <li>Return here and mark Step 1 complete again</li>
              </ol>
            </div>
          </div>
        </div>
      </CertificationStatusModal>

      {/* Step-4 gate error popup: shown when Mark Complete's attester-coverage
          validation fails (or the submit itself is rejected). Same wording the
          old danger ribbon used, promoted to a blocking modal so it cannot be
          missed above the fold. */}
      {step4GateError && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1060,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '700px', width: '100%', margin: '60px auto 0' }}>
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {step4GateError.title}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setStep4GateError(null)}></button>
              </div>
              <div className="modal-body">
                {step4GateError.details.map((detail, idx) => (
                  idx === step4GateError.details.length - 1
                    ? <p key={idx} className="mb-0 fw-bold">{detail}</p>
                    : <p key={idx}>{detail}</p>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setStep4GateError(null)}>
                  <i className="fas fa-times me-2"></i>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step-4 submit confirmation: Yes/Cancel gate in front of the
          point-of-no-return ATTESTATION transition. */}
      {showSubmitConfirmModal && (
        <div className="modal fade show" style={{
          display: 'block',
          backgroundColor: 'rgba(0,0,0,0.5)',
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 1060,
          overflow: 'auto'
        }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '600px', width: '100%', margin: '60px auto 0' }}>
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-paper-plane me-2"></i>
                  Submit Campaign for Attestation
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowSubmitConfirmModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to submit this campaign for attestation?</p>
                <ul className="mb-0">
                  <li>Email notifications will be sent to all assigned attesters.</li>
                  <li>Campaign status will change to <strong>ATTESTATION</strong>.</li>
                  <li>All workflow steps become <strong>read-only</strong> — this action cannot be undone.</li>
                </ul>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSubmitConfirmModal(false)}>
                  <i className="fas fa-times me-2"></i>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSubmitCampaignForAttestation}>
                  <i className="fas fa-check me-2"></i>
                  Yes, Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignWorkflow;
